import AddIcon from '@mui/icons-material/Add'
import CheckIcon from '@mui/icons-material/Check'
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined'
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1'
import PersonOutlineIcon from '@mui/icons-material/PersonOutlined'
import RefreshIcon from '@mui/icons-material/Refresh'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import {
    Autocomplete,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    InputAdornment,
    Menu,
    MenuItem,
    TextField,
} from '@mui/material'
import type { SxProps, Theme } from '@mui/material'
import React from 'react'
import FormStepper from '../../../components/form-stepper'
import GrantContent from '../../../components/GrantContent'
import type { CotizacionStatus, FormaPago, Genero, Parentesco } from '../../../model/kartera-api-model'
import KarteraAdminTemplate from '../../../template/KarteraAdminTemplate'
import KarteraPageHeader from '../../../template/kartera/KarteraPageHeader'
import { CheckIcon as CheckMarkIcon } from '../../../template/kartera/icons'
import { lookupName } from '../../../utils/lookups'
import * as Permisos from '../../../utils/Permisos'
import type { ValueRow } from '../../../utils/value-rows'
import CustomerForm from '../../customers/form/customer-form'
import ProspectForm from '../../prospects/form/prospect-form'
import {
    aseguradoraLabel,
    CARACTERISTICA_VALOR_MAX,
    COBERTURA_VALOR_MAX,
    COTIZACION_STATUS,
    DESCRIPCION_MAX,
    FORMA_PAGO,
    RamoMark,
    RAMOS,
    selectMenu,
} from '../common/quote-common'
import type { QuoteInsurerOption } from '../common/quote-common'
import { GENEROS, LAST_STEP, PARENTESCOS, TEXTO_MAX, useQuoteFormModel } from './quote-form-model'
import type { QuoteContratante, QuoteItemForm, ValueListKind } from './quote-form-model'

const newContratanteBtnSx: SxProps<Theme> = {
    flex: 'none',
    height: 52,
    width: 52,
    minWidth: 52,
    padding: 0,
    marginTop: 'calc(15px * 1.25 + 8px)',
    borderRadius: '12px',
    border: 0,
    boxShadow: 'none',
    backgroundColor: 'rgb(var(--k-accent-soft))',
    color: 'rgb(var(--k-accent-ink))',
    '& .MuiSvgIcon-root': { color: 'inherit' },
    '&:hover': {
        backgroundColor: 'rgb(var(--k-accent-soft))',
        color: 'rgb(var(--k-accent-ink))',
        filter: 'brightness(0.97)',
        boxShadow: 'none',
    },
}

const amountHtmlInput = { inputMode: 'decimal' as const, style: { textAlign: 'right' as const, fontVariantNumeric: 'tabular-nums' } }
const moneyAdornment = { input: { startAdornment: <InputAdornment position="start">$</InputAdornment> }, htmlInput: amountHtmlInput }

const cardClass = 'rounded-[18px] border border-k-line bg-k-surface'
const plusIcon = <AddIcon style={{ fontSize: 16 }} />

type QuoteFormModel = ReturnType<typeof useQuoteFormModel>
type ProposalView = QuoteFormModel['proposals'][number]

const Handle = (props: React.HTMLAttributes<HTMLSpanElement> & { draggable?: boolean }) => (
    <span className="ce-handle" title="Arrastra para ordenar" {...props}>
        <DragIndicatorIcon style={{ fontSize: 18 }} />
    </span>
)

const RemoveButton = ({ label, onClick }: { label: string; onClick: () => void }) => (
    <button type="button" className="ce-del" aria-label={label} title={label} onClick={onClick}>
        <DeleteOutlinedIcon style={{ fontSize: 16 }} />
    </button>
)

const GeneralStep = ({ model }: { model: QuoteFormModel }) => {
    const { form, se, change, contratanteLookup } = model
    return (
        <section className={`${cardClass} px-5 py-[18px]`}>
            <div className="grid grid-cols-1 items-start gap-x-4 gap-y-3.5 sm:grid-cols-2">
                <div className="flex items-start gap-2.5">
                    <Autocomplete<QuoteContratante>
                        className="min-w-0 flex-1"
                        options={contratanteLookup.options}
                        loading={contratanteLookup.searching}
                        value={form.contratante}
                        inputValue={contratanteLookup.inputValue}
                        onInputChange={(_, next, reason) => contratanteLookup.changeInput(next, reason)}
                        onChange={(_, option) => change.contratante(option)}
                        groupBy={(option) => (option.tipo === 'cliente' ? 'Clientes' : 'Prospectos')}
                        getOptionLabel={(option) => lookupName(option)}
                        getOptionKey={(option) => option.key}
                        isOptionEqualToValue={(option, current) => option.key === current.key}
                        filterOptions={(options) => options}
                        noOptionsText={contratanteLookup.noOptionsText}
                        loadingText="Buscando..."
                        slotProps={{ paper: { className: 'v2-select-menu' } }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                className="v2-field"
                                variant="filled"
                                fullWidth
                                required
                                label="Contratante"
                                error={!!se.contratante}
                                helperText="Cliente o prospecto · mín. 3 letras"
                            />
                        )}
                    />
                    <GrantContent permisos={[Permisos.CLIENTES_W, Permisos.PROSPECTOS_W]}>
                        <Button
                            type="button"
                            variant="contained"
                            aria-label="Nuevo cliente o prospecto"
                            title="Nuevo cliente o prospecto"
                            aria-haspopup="menu"
                            onClick={model.openContratanteMenu}
                            sx={newContratanteBtnSx}
                        >
                            <PersonAddAlt1Icon style={{ fontSize: 20 }} />
                        </Button>
                    </GrantContent>
                    <Menu
                        anchorEl={model.contratanteMenu}
                        open={Boolean(model.contratanteMenu)}
                        onClose={model.closeContratanteMenu}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        slotProps={{ paper: { className: 'v2-select-menu' } }}
                    >
                        <GrantContent permisos={[Permisos.CLIENTES_W]}>
                            <MenuItem onClick={() => model.openNewContratante('cliente')}><AddIcon fontSize="small" className="mr-2" />Cliente</MenuItem>
                        </GrantContent>
                        <GrantContent permisos={[Permisos.PROSPECTOS_W]}>
                            <MenuItem onClick={() => model.openNewContratante('prospecto')}><AddIcon fontSize="small" className="mr-2" />Prospecto</MenuItem>
                        </GrantContent>
                    </Menu>
                </div>
                <TextField
                    className="v2-field" variant="filled" fullWidth required
                    type="email"
                    label="Correo para enviar la propuesta"
                    value={form.correo}
                    error={!!se.correo}
                    helperText={se.correo ? 'Captura un correo válido' : undefined}
                    onChange={(event) => change.correo(event.target.value)}
                    slotProps={{ htmlInput: { list: 'quote-correos' } }}
                />
                <datalist id="quote-correos">
                    {model.correoOptions.map((correo) => <option key={correo} value={correo} />)}
                </datalist>
                <TextField
                    className="v2-field v2-select" variant="filled" fullWidth select required
                    label="Ramo"
                    value={form.ramo}
                    disabled={model.isEdit}
                    error={!!se.ramo}
                    helperText={model.isEdit ? 'El ramo no se puede cambiar' : 'Al cambiarlo se reinician las propuestas'}
                    onChange={(event) => change.ramo(event.target.value)}
                    slotProps={{
                        select: {
                            displayEmpty: true,
                            ...selectMenu,
                            renderValue: (value) => {
                                if (!value) return <em className="text-k-muted">Selecciona el ramo</em>
                                return (
                                    <span className="inline-flex items-center gap-2.5">
                                        <RamoMark ramo={String(value)} size={18} />
                                        <span>{String(value)}</span>
                                    </span>
                                )
                            },
                        },
                    }}
                >
                    <MenuItem value=""><em>Selecciona el ramo</em></MenuItem>
                    {RAMOS.map((ramo) => (
                        <MenuItem key={ramo} value={ramo}>
                            <span className="inline-flex items-center gap-2.5 py-0.5"><RamoMark ramo={ramo} size={18} />{ramo}</span>
                        </MenuItem>
                    ))}
                </TextField>
                <TextField
                    className="v2-field v2-select" variant="filled" fullWidth select required
                    label="Moneda de la cotización"
                    value={form.moneda}
                    error={!!se.moneda}
                    helperText="Aplica a todas las propuestas"
                    onChange={(event) => change.moneda(event.target.value)}
                    slotProps={{ select: { displayEmpty: true, ...selectMenu } }}
                >
                    <MenuItem value=""><em>Seleccione</em></MenuItem>
                    {model.monedas.map((moneda) => (
                        <MenuItem key={moneda.id} value={moneda.abreviacion || ''}>{moneda.abreviacion} · {moneda.moneda}</MenuItem>
                    ))}
                </TextField>
                <TextField
                    className="v2-field" variant="filled" fullWidth required
                    type="date"
                    label="Vigencia de la cotización"
                    value={form.fecVencimiento}
                    error={!!se.fecVencimiento}
                    helperText={se.fecVencimiento ? 'No puede ser anterior a hoy' : undefined}
                    onChange={(event) => change.fecVencimiento(event.target.value)}
                    slotProps={{ htmlInput: { min: model.minVencimiento } }}
                />
                <TextField
                    className="v2-field v2-select" variant="filled" fullWidth select required
                    label="Estatus"
                    value={form.status}
                    onChange={(event) => change.status(event.target.value as CotizacionStatus)}
                    slotProps={{ select: selectMenu }}
                >
                    {COTIZACION_STATUS.map((status) => <MenuItem key={status.id} value={status.id}>{status.label}</MenuItem>)}
                </TextField>
            </div>
        </section>
    )
}

const AseguradosEditor = ({ model }: { model: QuoteFormModel }) => {
    const { form, se, asegurado, drag } = model
    return (
        <div className="flex flex-col">
            <div className="ce-head aseg"><span /><span>Nombre</span><span>Parentesco</span><span>Género</span><span className="text-right">Edad</span><span /></div>
            {form.asegurados.map((row, index) => {
                const titular = index === 0
                return (
                    <div
                        key={row.key}
                        {...drag.rowProps('asegurados', index, asegurado.moveTo, !titular)}
                        className={`ce-row aseg ${drag.isOver('asegurados', index) ? 'drop' : ''}`}
                    >
                        <Handle {...drag.handleProps('asegurados', index, !titular)} />
                        <input
                            className={`ce-in ${se[`a${index}nombre`] ? 'err' : ''}`}
                            value={row.nombre}
                            placeholder="Nombre completo"
                            aria-label={`Nombre del asegurado ${index + 1}`}
                            onChange={(event) => asegurado.nombre(row.key, event.target.value)}
                        />
                        <select
                            className={`ce-in ${se[`a${index}parentesco`] ? 'err' : ''}`}
                            value={row.parentesco}
                            disabled={titular}
                            aria-label={`Parentesco del asegurado ${index + 1}`}
                            onChange={(event) => asegurado.parentesco(row.key, event.target.value as Parentesco | '')}
                        >
                            <option value="">Parentesco</option>
                            {PARENTESCOS.filter((option) => titular || option.id !== 'titular').map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}
                        </select>
                        <select
                            className={`ce-in ${se[`a${index}genero`] ? 'err' : ''}`}
                            value={row.genero}
                            aria-label={`Género del asegurado ${index + 1}`}
                            onChange={(event) => asegurado.genero(row.key, event.target.value as Genero | '')}
                        >
                            <option value="">Género</option>
                            {GENEROS.map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}
                        </select>
                        <input
                            className="ce-in num"
                            value={row.edad}
                            inputMode="numeric"
                            placeholder="Edad"
                            aria-label={`Edad del asegurado ${index + 1}`}
                            onChange={(event) => asegurado.edad(row.key, event.target.value)}
                        />
                        {titular ? <span /> : <RemoveButton label="Quitar asegurado" onClick={() => asegurado.remove(row.key)} />}
                    </div>
                )
            })}
            <button type="button" className="ce-link mt-1.5 self-start" onClick={asegurado.add}>{plusIcon}Agregar asegurado</button>
        </div>
    )
}

const DetailStep = ({ model }: { model: QuoteFormModel }) => {
    const { form, fields, se, change } = model
    return (
        <section className={`${cardClass} flex flex-col gap-3 px-[18px] py-3.5`}>
            {fields.asegurados && <AseguradosEditor model={model} />}
            {fields.vehiculo && (
                <div className="grid grid-cols-1 items-start gap-x-4 gap-y-3.5 py-1 md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
                    <TextField
                        className="v2-field" variant="filled" fullWidth required
                        label="Descripción del vehículo"
                        placeholder="Ej. Nissan Versa Advance CVT"
                        value={form.vehiculo.descripcion}
                        error={!!se.vDescripcion}
                        onChange={(event) => change.vehiculoDescripcion(event.target.value)}
                    />
                    <TextField
                        className="v2-field" variant="filled" fullWidth required
                        label="Modelo (año)"
                        value={form.vehiculo.modelo}
                        error={!!se.vModelo}
                        helperText={se.vModelo ? 'Cuatro dígitos, ej. 2024' : undefined}
                        onChange={(event) => change.vehiculoModelo(event.target.value)}
                        slotProps={{ htmlInput: { inputMode: 'numeric' } }}
                    />
                </div>
            )}
            {fields.bien && (
                <TextField
                    className="v2-field" variant="filled" fullWidth required multiline minRows={8}
                    label="Detalle del bien asegurado"
                    placeholder="Ubicación, características y sumas que se quieren asegurar…"
                    value={form.texto}
                    error={!!se.texto}
                    helperText={`${form.texto.length}/${TEXTO_MAX}`}
                    onChange={(event) => change.texto(event.target.value)}
                />
            )}
            {!form.ramo && <p className="m-0 py-4 text-center text-sm text-k-muted">Primero elige el ramo en Datos generales.</p>}
        </section>
    )
}

const ValueListEditor = ({ model, row, index, view, kind }: { model: QuoteFormModel; row: QuoteItemForm; index: number; view: ProposalView; kind: ValueListKind }) => {
    const list = model.item.valueList(row.key, kind)
    const rows: ValueRow[] = row[kind]
    const coberturas = kind === 'coberturas'
    const showErr = Boolean(model.se[`i${index}${kind}`])
    const listId = `${row.key}:${kind}`
    const maxValor = coberturas ? COBERTURA_VALOR_MAX : CARACTERISTICA_VALOR_MAX
    const missing = (value: ValueRow, field: 'descripcion' | 'valor') => showErr && !value[field].trim() && Boolean(value.descripcion.trim() || value.valor.trim())

    return (
        <>
            {coberturas && (view.suggestions.length > 0 || view.copyFrom) && (
                <div className="flex flex-wrap items-center gap-1.5">
                    {view.suggestions.length > 0 && <span className="mr-0.5 text-xs font-bold text-k-muted">Comunes:</span>}
                    {view.suggestions.map((value) => (
                        <button key={value} type="button" className="ce-chip" onClick={() => model.item.addSuggestion(row.key, value)}>+ {value}</button>
                    ))}
                    {view.copyFrom && (
                        <button type="button" className="ce-link ml-auto" onClick={() => model.item.copyCoberturas(row.key, view.copyFrom!.key)}>
                            Copiar de la propuesta {view.copyFrom.n}
                        </button>
                    )}
                </div>
            )}
            <div>
                <div className="ce-head"><span /><span>{coberturas ? 'Cobertura' : 'Característica'}</span><span>{coberturas ? 'Suma o valor' : 'Valor'}</span><span /></div>
                {rows.map((value, rowIndex) => (
                    <div
                        key={value.key}
                        {...model.drag.rowProps(listId, rowIndex, list.moveTo)}
                        className={`ce-row ${model.drag.isOver(listId, rowIndex) ? 'drop' : ''}`}
                    >
                        <Handle {...model.drag.handleProps(listId, rowIndex)} />
                        <input
                            className={`ce-in ${missing(value, 'descripcion') ? 'err' : ''}`}
                            value={value.descripcion}
                            maxLength={DESCRIPCION_MAX}
                            placeholder={coberturas ? 'Cobertura' : 'Característica'}
                            aria-label={`${coberturas ? 'Cobertura' : 'Característica'} ${rowIndex + 1}`}
                            onChange={(event) => list.change(value.key, { descripcion: event.target.value })}
                        />
                        <input
                            className={`ce-in num ${missing(value, 'valor') ? 'err' : ''}`}
                            value={value.valor}
                            maxLength={maxValor}
                            placeholder={coberturas ? 'Suma o valor' : 'Valor'}
                            aria-label={`Valor ${rowIndex + 1}`}
                            onChange={(event) => list.change(value.key, { valor: event.target.value })}
                        />
                        <RemoveButton label="Quitar renglón" onClick={() => list.remove(value.key)} />
                    </div>
                ))}
                <div key={list.draftKey} className="ce-row">
                    <Handle draggable={false} aria-disabled />
                    <input
                        className="ce-in"
                        value=""
                        maxLength={DESCRIPCION_MAX}
                        placeholder={coberturas ? '+ Nueva cobertura' : '+ Nueva característica'}
                        aria-label={coberturas ? 'Agregar cobertura' : 'Agregar característica'}
                        onChange={(event) => list.changeDraft({ descripcion: event.target.value })}
                    />
                    <input
                        className="ce-in num"
                        value=""
                        maxLength={maxValor}
                        placeholder="Valor"
                        aria-label="Agregar valor"
                        onChange={(event) => list.changeDraft({ valor: event.target.value })}
                    />
                    <span />
                </div>
                <span className="mt-1.5 block text-xs font-medium text-k-muted">Escribe en el renglón vacío para agregar otro · arrastra <span aria-hidden="true">⋮⋮</span> para reordenar</span>
            </div>
        </>
    )
}

const PlansEditor = ({ model, row, index, view }: { model: QuoteFormModel; row: QuoteItemForm; index: number; view: ProposalView }) => {
    const { planPago, drag, se } = model
    const listId = `${row.key}:planes`
    const err = (planIndex: number, field: string) => (se[`p${index}-${planIndex}${field}`] ? 'err' : '')
    return (
        <>
            {row.planesPago.length === 0 && (
                <div role="alert" className="rounded-[10px] bg-k-danger-soft px-3 py-2.5 text-[13px] font-semibold text-k-danger-ink">Cada propuesta necesita al menos un plan de pago.</div>
            )}
            {view.planWarning && (
                <div role="status" className="rounded-[10px] bg-tone-orange px-3 py-2.5 text-[13px] font-semibold text-tone-orange-ink">{view.planWarning}</div>
            )}
            <div>
                <div className="ce-head pago"><span /><span>Forma de pago</span><span className="text-right">Prima total</span><span>Recibos</span><span /></div>
                {row.planesPago.map((plan, planIndex) => {
                    const fraccionado = Boolean(plan.formaPago) && plan.formaPago !== 'contado'
                    return (
                        <div
                            key={plan.key}
                            {...drag.rowProps(listId, planIndex, (from, to) => planPago.moveTo(row.key, from, to))}
                            className={`border-b border-k-line ${drag.isOver(listId, planIndex) ? 'shadow-[inset_0_2px_0_rgb(var(--k-accent-ink))]' : ''}`}
                        >
                            <div className="ce-row pago border-b-0">
                                <Handle {...drag.handleProps(listId, planIndex)} />
                                <select
                                    className={`ce-in ${err(planIndex, 'formaPago')}`}
                                    value={plan.formaPago}
                                    aria-label={`Forma de pago del plan ${planIndex + 1}`}
                                    onChange={(event) => planPago.formaPago(row.key, plan.key, event.target.value as FormaPago | '')}
                                >
                                    <option value="">Forma de pago</option>
                                    {FORMA_PAGO.map((option) => <option key={option.key} value={option.key}>{option.label}</option>)}
                                </select>
                                <input
                                    className={`ce-in num ${err(planIndex, 'total')}`}
                                    value={plan.total}
                                    disabled={plan.formaPago !== 'contado'}
                                    inputMode="decimal"
                                    placeholder="$0.00"
                                    title={fraccionado ? 'Se calcula con los recibos' : undefined}
                                    aria-label={`Prima total del plan ${planIndex + 1}`}
                                    onChange={(event) => planPago.amount(row.key, plan.key, 'total', event.target.value)}
                                    onFocus={(event) => event.target.select()}
                                    onBlur={() => planPago.amountBlur(row.key, plan.key, 'total')}
                                />
                                <span className="truncate px-1.5 text-[12.5px] text-k-muted">{view.planSummaries[planIndex]}</span>
                                <RemoveButton label="Quitar plan de pago" onClick={() => planPago.remove(row.key, plan.key)} />
                            </div>
                            {fraccionado && (
                                <div className="ce-row pago min-h-0 border-b-0 pb-1.5">
                                    <span />
                                    <label className="flex min-w-0 items-center gap-2 text-xs font-semibold text-k-muted">
                                        <span className="shrink-0">Primer recibo</span>
                                        <input
                                            className={`ce-in num ${err(planIndex, 'primaPrimerRecibo')}`}
                                            value={plan.primaPrimerRecibo}
                                            inputMode="decimal"
                                            placeholder="$0.00"
                                            onChange={(event) => planPago.amount(row.key, plan.key, 'primaPrimerRecibo', event.target.value)}
                                            onFocus={(event) => event.target.select()}
                                            onBlur={() => planPago.amountBlur(row.key, plan.key, 'primaPrimerRecibo')}
                                        />
                                    </label>
                                    <label className="flex min-w-0 items-center gap-2 text-xs font-semibold text-k-muted">
                                        <span className="shrink-0">Subsecuentes ({model.parcialidades(plan.formaPago) - 1})</span>
                                        <input
                                            className={`ce-in num ${err(planIndex, 'primaReciboSub')}`}
                                            value={plan.primaReciboSub}
                                            inputMode="decimal"
                                            placeholder="$0.00"
                                            onChange={(event) => planPago.amount(row.key, plan.key, 'primaReciboSub', event.target.value)}
                                            onFocus={(event) => event.target.select()}
                                            onBlur={() => planPago.amountBlur(row.key, plan.key, 'primaReciboSub')}
                                        />
                                    </label>
                                    <span />
                                    <span />
                                </div>
                            )}
                        </div>
                    )
                })}
                <button type="button" className="ce-link mt-1.5" onClick={() => planPago.add(row.key)}>{plusIcon}Agregar plan de pago</button>
            </div>
        </>
    )
}

const ProposalCard = ({ model, row, index, view }: { model: QuoteFormModel; row: QuoteItemForm; index: number; view: ProposalView }) => {
    const { item, se } = model
    const extraFields = model.planFields.filter((field) => field.key !== 'sumaAsegurada')
    return (
        <section className={`ce-prop ${view.open ? 'open' : ''}`}>
            <div className="flex items-center gap-1 pr-2.5">
                <button type="button" className="ce-sum" onClick={() => item.toggle(row.key)} aria-expanded={view.open}>
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[11px] bg-k-accent-soft text-sm font-extrabold text-k-accent-ink">{view.n}</span>
                    <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                        <span className="truncate text-[15px] font-extrabold text-k-ink">{view.title}</span>
                        <span className="truncate text-[12.5px] text-k-muted">{view.sub}</span>
                    </span>
                    <span className={`shrink-0 whitespace-nowrap rounded-full px-[9px] py-[3px] text-[11px] font-extrabold ${view.ok ? 'bg-k-ok-soft text-k-ok' : 'bg-tone-orange text-tone-orange-ink'}`}>{view.status}</span>
                    <span className="hidden min-w-[92px] flex-col items-end sm:flex">
                        <span className="text-[11px] text-k-muted">Desde</span>
                        <span className="text-[15px] font-extrabold tabular-nums text-k-ink">{view.desde}</span>
                    </span>
                </button>
                <button type="button" className="ce-ico" onClick={() => item.duplicate(row.key)} aria-label={`Duplicar propuesta ${view.n}`} title="Duplicar">
                    <ContentCopyOutlinedIcon style={{ fontSize: 16 }} />
                </button>
                <button type="button" className="ce-ico danger" onClick={() => item.remove(row.key)} aria-label={`Quitar propuesta ${view.n}`} title="Quitar">
                    <DeleteOutlinedIcon style={{ fontSize: 17 }} />
                </button>
            </div>

            {view.open && (
                <div className="border-t border-k-line">
                    <div className="flex flex-col gap-3 px-4 py-3.5">
                        {row.id == null && model.templates.length > 0 && (
                            <TextField
                                className="v2-field v2-select" variant="filled" fullWidth select
                                label="Llenar con un template"
                                value={row.templateId === '' ? '' : String(row.templateId)}
                                onChange={(event) => item.template(row.key, event.target.value ? Number(event.target.value) : '')}
                                slotProps={{ select: { displayEmpty: true, ...selectMenu } }}
                            >
                                <MenuItem value=""><em>Sin template</em></MenuItem>
                                {model.templates.map((template) => (
                                    <MenuItem key={template.id} value={String(template.id)}>
                                        {template.nombre || 'Template'}{template.aseguradora ? ` · ${template.aseguradora}` : ''}
                                    </MenuItem>
                                ))}
                            </TextField>
                        )}
                        <div className="grid grid-cols-1 items-start gap-x-3.5 gap-y-3 sm:grid-cols-2">
                            <Autocomplete<QuoteInsurerOption>
                                options={model.aseguradoras}
                                value={row.aseguradora}
                                onChange={(_, option) => item.aseguradora(row.key, option)}
                                getOptionLabel={(option) => aseguradoraLabel(option)}
                                isOptionEqualToValue={(option, current) => option.id === current.id}
                                noOptionsText="Sin resultados"
                                slotProps={{ paper: { className: 'v2-select-menu' } }}
                                renderInput={(params) => (
                                    <TextField {...params} className="v2-field" variant="filled" fullWidth required label="Aseguradora" error={!!se[`i${index}aseguradora`]} />
                                )}
                            />
                            <TextField
                                className="v2-field" variant="filled" fullWidth required
                                label="Plan o paquete"
                                placeholder="Ej. Básico"
                                value={row.plan}
                                error={!!se[`i${index}plan`]}
                                onChange={(event) => item.plan(row.key, event.target.value)}
                            />
                            <TextField
                                className="v2-field" variant="filled" fullWidth required
                                label="Suma asegurada"
                                placeholder="0.00"
                                value={row.sumaAsegurada}
                                error={!!se[`i${index}sumaAsegurada`]}
                                onChange={(event) => item.field(row.key, 'sumaAsegurada', event.target.value)}
                                onFocus={(event) => event.target.select()}
                                onBlur={() => item.fieldBlur(row.key, 'sumaAsegurada')}
                                slotProps={moneyAdornment}
                            />
                            {extraFields.map((field) => (
                                <TextField
                                    key={field.key}
                                    className="v2-field" variant="filled" fullWidth
                                    label={field.label}
                                    value={row[field.key]}
                                    placeholder={field.amount ? '0.00' : undefined}
                                    onChange={(event) => item.field(row.key, field.key, event.target.value)}
                                    onFocus={field.amount ? (event) => event.target.select() : undefined}
                                    onBlur={() => item.fieldBlur(row.key, field.key)}
                                    slotProps={field.amount ? moneyAdornment : undefined}
                                />
                            ))}
                        </div>
                    </div>

                    <div role="tablist" aria-label={`Detalle de la propuesta ${view.n}`} className="ce-tabs">
                        {view.tabs.map((tab) => {
                            const on = view.tab === tab.id
                            return (
                                <button key={tab.id} type="button" role="tab" aria-selected={on} className="ce-tab" onClick={() => item.tab(row.key, tab.id)}>
                                    {tab.label}
                                    <span className={`inline-flex h-5 min-w-[20px] items-center justify-center rounded-[10px] px-1.5 text-[11px] font-extrabold ${on ? 'bg-k-accent-soft text-k-accent-ink' : 'bg-k-line text-k-ink-2'}`}>{tab.count}</span>
                                    {tab.alert && <span aria-label="Falta información" className="h-2 w-2 rounded-full bg-k-danger" />}
                                </button>
                            )
                        })}
                    </div>
                    <div role="tabpanel" className="flex flex-col gap-2.5 px-4 pb-4 pt-3">
                        {view.tab === 'planes'
                            ? <PlansEditor model={model} row={row} index={index} view={view} />
                            : <ValueListEditor model={model} row={row} index={index} view={view} kind={view.tab} />}
                    </div>
                </div>
            )}
        </section>
    )
}

const ProposalsStep = ({ model }: { model: QuoteFormModel }) => {
    const { form, item } = model
    return (
        <>
            {form.items.length === 0 && (
                <p className={`m-0 rounded-[18px] border border-dashed px-5 py-6 text-center text-sm ${model.se.items ? 'border-k-danger text-k-danger-ink' : 'border-k-line-strong text-k-muted'}`}>
                    {form.ramo ? 'Agrega al menos una propuesta para que el cliente compare.' : 'Primero elige el ramo en Datos generales.'}
                </p>
            )}
            {form.items.map((row, index) => (
                <ProposalCard key={row.key} model={model} row={row} index={index} view={model.proposals[index]} />
            ))}
            {form.ramo && (
                <div className="flex flex-wrap gap-2">
                    <button
                        type="button"
                        onClick={item.add}
                        className="flex h-12 flex-[1_1_200px] cursor-pointer items-center justify-center gap-2 rounded-[14px] border-[1.5px] border-dashed border-k-line-strong bg-transparent text-sm font-bold text-k-ink hover:bg-k-hover"
                    >
                        <AddIcon style={{ fontSize: 18 }} />Agregar propuesta
                    </button>
                    {model.openIndex >= 0 && (
                        <button
                            type="button"
                            onClick={() => item.duplicate(form.items[model.openIndex].key)}
                            className="h-12 flex-[1_1_200px] cursor-pointer rounded-[14px] border border-k-line-strong bg-k-surface text-sm font-bold text-k-ink hover:bg-k-hover"
                        >
                            Duplicar la propuesta {model.openIndex + 1}
                        </button>
                    )}
                </div>
            )}
        </>
    )
}

const QuoteSummary = ({ model }: { model: QuoteFormModel }) => {
    const { resumen } = model
    return (
        <aside className="cl-aside flex w-full min-w-0 flex-col gap-3 rounded-[20px] border border-k-line bg-k-surface p-4" aria-label="Resumen de la cotización">
            <div className="flex min-w-0 items-center gap-2.5">
                {resumen.ramo
                    ? <RamoMark ramo={resumen.ramo} boxed />
                    : <span className="flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-[11px] bg-tone-purple text-tone-purple-ink"><PersonOutlineIcon style={{ fontSize: 18 }} /></span>}
                <span className="flex min-w-0 flex-col">
                    <span className="truncate text-xs font-bold text-k-muted">{resumen.ramo || 'Cotización'}{resumen.numero ? ` · Cotización ${resumen.numero}` : ''}</span>
                    <span className="truncate text-sm font-extrabold text-k-ink">{resumen.contratante || 'Sin contratante'}</span>
                </span>
            </div>
            <div className="flex flex-col gap-0.5 rounded-[14px] bg-k-accent-soft px-4 py-3.5">
                <span className="text-xs font-bold text-k-accent-ink">Desde</span>
                <span className="text-[26px] font-extrabold tracking-[-0.02em] tabular-nums text-k-ink">{resumen.desde}</span>
                <span className="text-[12.5px] text-k-ink-2">{resumen.conteo}</span>
            </div>
            {model.proposals.length > 0 && (
                <div className="flex flex-col gap-1.5">
                    {model.proposals.map((row, index) => (
                        <button
                            key={row.key}
                            type="button"
                            onClick={() => model.openProposal(index)}
                            className="flex min-h-10 cursor-pointer items-center gap-2.5 rounded-[10px] border-0 bg-transparent px-1.5 py-1 text-left text-k-ink hover:bg-k-hover"
                        >
                            <span
                                className="flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full"
                                style={row.ok ? { background: 'rgb(var(--k-ok))' } : { border: `2px solid ${resumen.tried ? 'rgb(var(--k-danger))' : '#E0A23A'}` }}
                            >
                                {row.ok && <CheckIcon style={{ fontSize: 11, color: '#fff' }} />}
                            </span>
                            <span className="flex min-w-0 flex-1 flex-col">
                                <span className="truncate text-[13px] font-bold">{row.n}. {row.title}</span>
                                <span className={`text-xs ${row.ok ? 'text-k-muted' : resumen.tried ? 'text-k-danger' : 'text-tone-orange-ink'}`}>
                                    {row.ok ? `Desde ${row.desde}` : `Falta: ${row.issues.join(', ')}`}
                                </span>
                            </span>
                        </button>
                    ))}
                </div>
            )}
            <dl className="m-0 flex flex-col gap-1.5 border-t border-k-line pt-2.5 text-[13px]">
                <div className="flex justify-between gap-2"><dt className="text-k-muted">Enviar a</dt><dd className="m-0 break-all text-right font-semibold text-k-ink">{resumen.correo || '—'}</dd></div>
                <div className="flex justify-between gap-2"><dt className="text-k-muted">Vence</dt><dd className="m-0 font-semibold text-k-ink">{resumen.vence}</dd></div>
            </dl>
            {(model.errList.length > 0 || resumen.pendientes.length > 0) && (
                <div role="alert" className="rounded-[10px] bg-k-danger-soft px-3 py-2.5 text-[13px] font-semibold leading-[1.45] text-k-danger-ink">
                    {[model.errList.length ? `Falta: ${model.errList.join(', ')}` : '', ...resumen.pendientes].filter(Boolean).join(' · ')}
                </div>
            )}
            <button
                type="button"
                onClick={model.save}
                disabled={model.loading}
                className="flex h-[50px] cursor-pointer items-center justify-center gap-2 rounded-[14px] border-0 bg-k-primary text-[15px] font-extrabold text-white hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-50"
            >
                <CheckMarkIcon />Guardar cotización
            </button>
            {model.previewHref && (
                <a
                    href={model.previewHref}
                    target="_blank"
                    rel="noreferrer"
                    className="flex h-[42px] items-center justify-center gap-1.5 rounded-xl border border-k-line-strong text-sm font-bold text-k-ink no-underline hover:bg-k-hover"
                >
                    <VisibilityOutlinedIcon style={{ fontSize: 16 }} />Vista previa para el cliente
                </a>
            )}
            <button type="button" onClick={model.cancel} className="flex min-h-8 cursor-pointer items-center self-center border-0 bg-transparent text-[13px] font-bold text-k-accent-ink hover:underline">
                Cancelar
            </button>
        </aside>
    )
}

const QuoteForm = () => {
    const model = useQuoteFormModel()

    return (
        <KarteraAdminTemplate loading={false} title={model.title}>
            <Dialog open={model.newContratante === 'cliente'} onClose={model.closeNewContratante} fullWidth maxWidth="md" slotProps={{ paper: { className: 'k-dialog' } }}>
                <DialogTitle>Alta de cliente</DialogTitle>
                <DialogContent>
                    <CustomerForm modal onCompleted={model.clienteCreated} />
                </DialogContent>
            </Dialog>

            <Dialog open={model.newContratante === 'prospecto'} onClose={model.closeNewContratante} fullWidth maxWidth="md" slotProps={{ paper: { className: 'k-dialog' } }}>
                <DialogTitle>Alta de prospecto</DialogTitle>
                <DialogContent>
                    <ProspectForm modal onCompleted={model.prospectoCreated} />
                </DialogContent>
            </Dialog>

            <div className="relative mx-auto flex w-full max-w-[1360px] flex-col gap-3">
                {model.loading && (
                    <div className="absolute inset-0 z-40 flex items-start justify-center rounded-2xl bg-k-bg/80 pt-24" role="status">
                        <div className="flex flex-col items-center gap-3 rounded-2xl border border-k-line bg-k-surface px-8 py-6 shadow-k-pop">
                            <RefreshIcon className="animate-spin text-brand-indigo" />
                            <p className="m-0 text-sm font-semibold text-k-ink">{model.busyLabel}</p>
                        </div>
                    </div>
                )}

                <KarteraPageHeader
                    title={model.title}
                    backTo={model.backTo}
                    backLabel="Cotizaciones"
                    actions={<span className="text-xs text-k-muted"><span className="text-k-danger">*</span> Obligatorio</span>}
                />

                {model.message && (
                    <div className={`flex items-center gap-3 rounded-[14px] px-4 py-3.5 text-sm ${model.message.type === 'error' ? 'bg-k-danger-soft text-k-danger-ink' : 'bg-k-ok-soft text-k-ok'}`}>
                        <ErrorOutlineIcon style={{ fontSize: 20 }} />
                        <p className="m-0">{model.message.message}</p>
                    </div>
                )}

                <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,1fr)_300px]">
                    <div className="flex min-w-0 flex-col gap-3">
                        <FormStepper labels={model.steps.labels} step={model.step} maxStep={model.maxStep} isOk={model.okSec} onStep={model.goToStep} />

                        {model.step === 0 && <GeneralStep model={model} />}
                        {model.step === 1 && <DetailStep model={model} />}
                        {model.step === LAST_STEP && <ProposalsStep model={model} />}

                        {model.stepMissing.length > 0 && (
                            <div role="alert" className="flex items-start gap-2.5 rounded-[14px] bg-k-danger-soft px-4 py-3 text-sm text-k-danger-ink">
                                <ErrorOutlineIcon style={{ fontSize: 20, flexShrink: 0 }} />
                                <span className="leading-snug">
                                    <strong className="font-extrabold">Para continuar, completa {model.stepMissing.length === 1 ? 'este campo' : `estos ${model.stepMissing.length} campos`}:</strong>{' '}
                                    {model.stepMissing.join(', ')}.
                                </span>
                            </div>
                        )}

                        <div className="flex gap-2.5">
                            {model.step > 0 && (
                                <button type="button" onClick={model.stepBack} className="h-12 flex-1 cursor-pointer rounded-[14px] border border-k-line-strong bg-k-surface text-[15px] font-bold text-k-ink hover:bg-k-hover">
                                    Atrás
                                </button>
                            )}
                            {model.step < LAST_STEP && (
                                <button type="button" onClick={model.stepNext} className="h-12 flex-[2] cursor-pointer rounded-[14px] border-0 bg-k-primary text-[15px] font-extrabold uppercase text-white hover:brightness-105">
                                    Continuar
                                </button>
                            )}
                        </div>
                    </div>

                    <QuoteSummary model={model} />
                </div>
            </div>

            {model.toast && (
                <div role="status" className="fixed bottom-6 left-1/2 z-[70] flex -translate-x-1/2 items-center gap-2.5 rounded-xl bg-[#1B1D29] px-4 py-3 text-sm text-white shadow-[0_12px_32px_rgba(10,12,20,0.3)]">
                    <CheckIcon style={{ fontSize: 17, color: '#3FD3C6' }} />
                    <span>{model.toast.message}</span>
                    {model.toast.undo && (
                        <button type="button" onClick={model.undo} className="min-h-8 cursor-pointer border-0 bg-transparent text-[13px] font-extrabold text-[#3FD3C6]">Deshacer</button>
                    )}
                </div>
            )}
        </KarteraAdminTemplate>
    )
}

export default QuoteForm
